import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Upload as AntDUpload, Button, message } from "antd";
import { UploadOutlined, InboxOutlined, PlusOutlined } from "@ant-design/icons";

import { UploadCardTextContainer } from "./Upload.styles";

import { getPresignedPostData } from "apis/s3Upload";

import Locale from "react-intl-universal";

const FILE_STATUS_DONE = "done";
const FILE_STATUS_ERROR = "error";
const FILE_STATUS_REMOVED = "removed";

const getFileSizeInMb = (file) => {
  return file.size / 1024 / 1024;
};

const ButtonUpload = ({
  isDisplayCard = false,
  isUploading = false,
  label,
}) => {
  return isDisplayCard ? (
    <div>
      <PlusOutlined />
      <UploadCardTextContainer>
        {isUploading ? "Uploading..." : label}
      </UploadCardTextContainer>
    </div>
  ) : (
    <>
      <p className="ant-upload-drag-icon">
        <InboxOutlined />
      </p>
      <p className="ant-upload-text">{Locale.get("uploadDragger.dragArea")}</p>
      <p className="ant-upload-hint">
        {Locale.get("uploadDragger.dragAreaContent")}
      </p>
    </>
  );
};

const Upload = ({
  value: formValue,
  acceptTypes,
  fileSizeLimitInMb,
  labelButtonUpload = "Upload images / documents",

  isDisabled = false,
  isMultiple = false,
  isDisplayCard = false,

  onChange = () => {},
  onFileUploadChange = () => {},
  updateFormValue = () => {},
}) => {
  const [isInitWithFormValue, setIsInitWithFormValue] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [files, setFiles] = useState([]);
  const [s3Url, setS3Url] = useState("");
  const [presignedFormData, setPresignedFormData] = useState({});
  const fileUrl = useMemo(
    () => `${s3Url}/${encodeURIComponent(presignedFormData.key)}`,
    [s3Url, presignedFormData]
  );

  useEffect(() => {
    if (!isInitWithFormValue && formValue) {
      const filesToUpdate = isMultiple ? formValue : [formValue];
      setIsInitWithFormValue(true);
      setFiles(filesToUpdate);
    }
  }, [formValue, isInitWithFormValue, isMultiple]);

  const updateValue = (newValue) => {
    let valueToUpdateExternal = newValue;
    if (!isMultiple) {
      valueToUpdateExternal = newValue[0];
    }

    onChange(valueToUpdateExternal);
    updateFormValue(valueToUpdateExternal);
    setFiles(newValue);
  };

  const handleOnFileUploadChange = (isUploading) => {
    setIsUploading(isUploading);
    onFileUploadChange(isUploading);
  };

  const handleBeforeUpload = async (file) => {
    handleOnFileUploadChange(true);

    try {
      if (fileSizeLimitInMb && getFileSizeInMb(file) >= fileSizeLimitInMb) {
        const errMessage = `Image must be smaller than ${fileSizeLimitInMb}MB`;
        message.error(errMessage);

        throw new Error(errMessage);
      }

      const { data: presignedPostData } = await getPresignedPostData(
        file
      ).catch((ex) => {
        message.error(
          `${file.name} ${Locale.get("uploadDragger.fileUploadFail")}`
        );
        throw ex;
      });
      setS3Url(presignedPostData.url);
      setPresignedFormData(presignedPostData.fields);
      return true;
    } catch (ex) {
      handleOnFileUploadChange(false);
      throw ex;
    }
  };

  const handleOnFileChange = ({ event, file: currentFile }) => {
    if (!event) {
      if (currentFile.status === FILE_STATUS_DONE) {
        message.destroy();
        message.success(
          `${currentFile.name} ${Locale.get("uploadDragger.fileUploadSuccess")}`
        );
        const fileToUpdate = {
          name: currentFile.name,
          uid: currentFile.uid,
          url: fileUrl,
        };
        if (isMultiple) {
          updateValue([...files, fileToUpdate]);
        } else {
          updateValue([fileToUpdate]);
        }

        handleOnFileUploadChange(false);
      } else if (currentFile.status === FILE_STATUS_REMOVED) {
        if (isMultiple) {
          const indexOfFileToDelete = files.findIndex(
            (value) => value.uid === currentFile.uid
          );
          const removedFiles = files.slice(
            indexOfFileToDelete,
            indexOfFileToDelete + 1
          );
          const newFilesToUpdate = files.filter(
            (value) =>
              !removedFiles.find((removedFile) => removedFile.uid === value.uid)
          );

          updateValue(newFilesToUpdate);
        } else {
          updateValue([]);
        }

        handleOnFileUploadChange(false);
      } else if (currentFile.status === FILE_STATUS_ERROR) {
        handleOnFileUploadChange(false);
        return;
      }
    }
  };

  return (
    <AntDUpload.Dragger
      action={s3Url}
      accept={acceptTypes && acceptTypes.join(",")}
      beforeUpload={handleBeforeUpload}
      listType={isDisplayCard ? "picture-card" : "text"}
      data={{ ...presignedFormData }}
      headers={{}}
      disabled={isDisabled || isUploading}
      onChange={handleOnFileChange}
      fileList={files}
    >
      {!isDisabled && (
        <ButtonUpload
          isDisplayCard={isDisplayCard}
          isUploading={isUploading}
          label={labelButtonUpload}
        />
      )}
    </AntDUpload.Dragger>
  );
};

Upload.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      uid: PropTypes.string,
    }),
    PropTypes.array,
  ]),
  acceptTypes: PropTypes.array,
  fileSizeLimitInMb: PropTypes.number,
  labelButtonUpload: PropTypes.string,

  isDisabled: PropTypes.bool,
  isMultiple: PropTypes.bool,
  isDisplayCard: PropTypes.bool,

  onChange: PropTypes.func,
  onFileUploadChange: PropTypes.func,
  updateFormValue: PropTypes.func,
};

export default Upload;
